import React from "react";
import { motion } from "framer-motion";
import { Check, ArrowRight, Sparkles } from "lucide-react";

const plans = [
  {
    name: "Starter Website",
    price: "₹15,000",
    suffix: "+",
    tagline: "Perfect for small shops & local businesses",
    features: [
      "Up to 4 pages",
      "Mobile responsive design",
      "Contact form + WhatsApp button",
      "Basic SEO setup",
      "Free hosting guidance",
      "7-day delivery",
    ],
  },
  {
    name: "Business Website",
    price: "₹25,000",
    suffix: "+",
    tagline: "For growing businesses serious about leads",
    featured: true,
    features: [
      "Up to 8 pages",
      "Premium custom design",
      "Lead qualification forms",
      "On-page SEO + Google indexing",
      "Google Maps & analytics setup",
      "WhatsApp + payment integrations",
      "14-day delivery",
    ],
  },
  {
    name: "Premium Website",
    price: "₹40,000",
    suffix: "+",
    tagline: "For brands wanting a luxury online presence",
    features: [
      "Unlimited pages",
      "Bespoke animation & UX",
      "Blog / E-commerce module",
      "Advanced SEO + schema markup",
      "Performance optimisation (Lighthouse 95+)",
      "3-month free maintenance",
      "Priority support",
    ],
  },
];

export const Pricing = ({ onCTA }) => {
  return (
    <section
      id="pricing"
      data-testid="pricing-section"
      className="relative py-24 sm:py-32 border-t border-white/5"
    >
      <div className="absolute inset-0 grid-pattern opacity-30 pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 sm:px-8">
        <div className="max-w-2xl mb-16">
          <div className="text-[11px] tracking-[0.2em] uppercase text-[#D4AF37] mb-4">
            Transparent Pricing
          </div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-light text-white">
            Built for every <span className="gold-text italic">business stage.</span>
          </h2>
          <p className="mt-6 text-[#B0B0B0] max-w-xl">
            Fixed-quote packages with no hidden fees. Final cost depends on scope —
            you'll get a clear price after the free consultation.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-5">
          {plans.map((p, i) => (
            <motion.div
              key={p.name}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.6, delay: i * 0.08 }}
              data-testid={`pricing-card-${i}`}
              className={`relative rounded-3xl p-8 flex flex-col transition-all duration-500 hover:scale-[1.02] ${
                p.featured
                  ? "border border-[#D4AF37]/40 hover:border-[#D4AF37]"
                  : "bg-[#0A0A0A] border border-[#222222] hover:border-[#D4AF37]/40 hover:-translate-y-1"
              }`}
              style={
                p.featured
                  ? {
                      background:
                        "linear-gradient(180deg, rgba(212,175,55,0.10) 0%, rgba(212,175,55,0.02) 100%)",
                    }
                  : undefined
              }
            >
              {p.featured && (
                <>
                  <div className="absolute -top-32 -right-20 w-72 h-72 bg-[#D4AF37]/15 blur-3xl rounded-full pointer-events-none" />
                  <span
                    data-testid="pricing-featured-badge"
                    className="absolute -top-3 left-8 inline-flex items-center gap-1.5 bg-[#D4AF37] text-black text-[10px] tracking-[0.18em] uppercase font-semibold rounded-full px-3 py-1.5"
                  >
                    <Sparkles className="w-3 h-3" /> Most Popular
                  </span>
                </>
              )}

              <div className="relative">
                <h3 className="font-display text-2xl text-white">{p.name}</h3>
                <p className="text-sm text-[#B0B0B0] mt-2 min-h-[40px]">{p.tagline}</p>

                <div className="mt-6 flex items-baseline gap-1">
                  <span className="font-display text-5xl text-white tracking-tighter">
                    {p.price}
                  </span>
                  <span className="text-[#D4AF37] text-lg font-medium">{p.suffix}</span>
                </div>
                <div className="text-xs text-[#737373] mt-1">one-time</div>

                <div className="my-6 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />

                <ul className="space-y-3">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-3 text-sm text-[#B0B0B0]">
                      <span className="w-5 h-5 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/30 flex items-center justify-center shrink-0 mt-0.5">
                        <Check className="w-3 h-3 text-[#D4AF37]" />
                      </span>
                      <span className="leading-relaxed">{f}</span>
                    </li>
                  ))}
                </ul>

                <button
                  data-testid={`pricing-cta-${i}`}
                  onClick={onCTA}
                  className={`btn-gold-glow mt-8 w-full inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 font-semibold transition-all ${
                    p.featured
                      ? "bg-[#D4AF37] hover:bg-[#E6C35C] text-black shadow-[0_0_30px_rgba(212,175,55,0.35)]"
                      : "bg-transparent text-white border border-[#D4AF37]/40 hover:border-[#D4AF37] hover:bg-[#D4AF37]/10"
                  }`}
                >
                  Get Started <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </motion.div>
          ))}
        </div>

        <p className="mt-10 text-center text-xs text-[#737373]">
          All packages are fully custom-coded. No templates. No page builders. You own
          100% of the code.
        </p>
      </div>
    </section>
  );
};
